import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { Dropdown } from 'react-bootstrap';
import { MessageChannel, MessagePushAndInboxSelector } from 'config/CustomEnums'
import BasePrompt from 'components/base/prompt/BasePrompt';
import CustomDateTimeSelect from 'components/base/CustomDateTimeSelect';
import RecurringMessagePromptBody from 'components/campaign/campaignCreation/RecurringMessagePromptBody'
import './RecurringMessages.scss'
import moment from "moment";
import { ReactHookFormErrorMessage } from "components/base/ErrorFieldMessage";
import ReactHookFormSwitch from "components/reactHookFormComponents/ReactHookFormSwitch";
import { SingleConditionRow } from "./AdditionalQuestionBar";

const channelConfig = {
    [MessageChannel.all]: {
        title: "",
        label: MessageChannel.all,
    },
    [MessageChannel.allWhatsapp]: {
        title: "",
        label: MessageChannel.allWhatsapp,
    },
    // [MessageChannel.allWechat]: {
    //     title: "",
    //     label: MessageChannel.allWechat,
    // },
    [MessageChannel.email]: {
        title: "Content for Email",
        label: MessageChannel.email,
    },
    [MessageChannel.sms]: {
        title: "Content for SMS",
        label: MessageChannel.sms,
    },
    [MessageChannel.whatsapp]: {
        title: "Content for Whatsapp",
        label: MessageChannel.whatsapp,
    },
    // [MessageChannel.wechat]: {
    //     title: "Content for WeChat",
    //     label: MessageChannel.wechat,
    // },
    [MessageChannel.pushAndInbox]: {
        title: "Content for Push and Inbox",
        label: "Push/Inbox",
    }
}

const whatsappAvailableChannels = [
    MessageChannel.pushAndInbox,
    MessageChannel.whatsapp,
    MessageChannel.email,
]

const wechatAvailableChannels = [
    MessageChannel.pushAndInbox,
    MessageChannel.wechat,
    MessageChannel.email,
]

const smsAvailableChannels = [
    MessageChannel.pushAndInbox,
    MessageChannel.sms,
    MessageChannel.email,
]

const possibleWhatsapp = [
    MessageChannel.allWhatsapp,
    ...whatsappAvailableChannels
]

const possibleWechat = [
    MessageChannel.allWechat,
    ...wechatAvailableChannels
]

const possibleSMS = [
    MessageChannel.all,
    ...smsAvailableChannels
]

function getAddableChannels({ selectedChannels }) {
    const allChannels = Object.keys(channelConfig)
    if (!selectedChannels) {
        return allChannels
    }
    const unselectedChannels = allChannels.filter(item => !selectedChannels.includes(item))
    let finalChannels = unselectedChannels
    if (selectedChannels.includes(MessageChannel.whatsapp) || selectedChannels.includes(MessageChannel.wechat)) {
        finalChannels = unselectedChannels.filter(item => item != MessageChannel.sms && item != MessageChannel.all)
    }
    if (selectedChannels.includes(MessageChannel.sms)) {
        finalChannels = unselectedChannels.filter(item => item != MessageChannel.whatsapp && item != MessageChannel.allWhatsapp&&item != MessageChannel.wechat && item != MessageChannel.allWechat)
    }
    if (selectedChannels.includes(MessageChannel.inbox) || selectedChannels.includes(MessageChannel.push)) {
        finalChannels = unselectedChannels.filter(item => item != MessageChannel.pushAndInbox)
    }
    return finalChannels
}

function getSelectedChannels({ selectedChannels }) {
    if (!selectedChannels) {
        return []
    }
    if (selectedChannels.includes(MessageChannel.all)) {
        return smsAvailableChannels
    }
    if (selectedChannels.includes(MessageChannel.allWhatsapp)) {
        return whatsappAvailableChannels
    }
    if (selectedChannels.includes(MessageChannel.allWechat)) {
        return wechatAvailableChannels
    }
    if (selectedChannels?.includes(MessageChannel.inbox) || selectedChannels?.includes(MessageChannel.push)) {
        const removePushAndInboxList = selectedChannels.filter(item => item != MessageChannel.inbox && item != MessageChannel.push)
        removePushAndInboxList.push(MessageChannel.pushAndInbox)
        return removePushAndInboxList
    }
    return selectedChannels
}

function disabledAddChannel({ selectedChannels }) {
    if (!selectedChannels) {
        return false
    }
    const allSMSChannelSelected = possibleSMS.every(item => selectedChannels.includes(item));
    if (allSMSChannelSelected) {
        return true
    }
    const allWhatsappChannelSelected = possibleWhatsapp.every(item => selectedChannels.includes(item));
    if (allWhatsappChannelSelected) {
        return true
    }
    const allWechatChannelSelected = possibleWechat.every(item => selectedChannels.includes(item));
    if (allWechatChannelSelected) {
        return true
    }
    return selectedChannels.some(
        (item) =>
          item == MessageChannel.all ||
          item == MessageChannel.allWhatsapp ||
          item == MessageChannel.allWechat,
      );
}

function AddChannel({ originalChannels, setValue }) {
    const selectedChannels = originalChannels || []
    const [showChannelDropDown, setShowChannelDropDown] = useState(false)
    const channels = getAddableChannels({ selectedChannels })
    const disableAdd = disabledAddChannel({ selectedChannels })
    const onChannelSelect = (option) => {
        console.log("onChannelSelect:", option)
        setValue(`recurringMessage.channels`, [...selectedChannels, option], { shouldDirty: true })
        if ([MessageChannel.all, MessageChannel.allWhatsapp, MessageChannel.allWechat].includes(option)) {
            setValue(`recurringMessage.pushAndInboxSelector`, MessagePushAndInboxSelector.both, { shouldDirty: true })
        }
        setShowChannelDropDown(false);
    }

    return (
        <Dropdown
            show={!disableAdd && showChannelDropDown}
            onToggle={(isOpen) => {
                if (disableAdd) {
                    return
                }
                setShowChannelDropDown(isOpen);
            }}
            className={`${disableAdd ? 'dropdown-section-disabled' : ''}`}
        >
            <Dropdown.Toggle
                className="btn-back-button-common content-add-button"
                variant="none"
                id={"dropdown-recurring-message-toggle"}
                disabled={disableAdd}
            >
                + Add Message Channel
            </Dropdown.Toggle>

            <Dropdown.Menu style={{ zIndex: 2 }} className="channel-dropdown">
                {channels.map((option, index) => (
                    <Dropdown.Item
                        key={`${index}-${option.label}`}
                        className="filterable-item"
                        onSelect={(eventKey) => {
                            onChannelSelect(option);
                        }}
                        eventKey={index}
                    >
                        {channelConfig[option]?.label}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    )
}

function SingleChannel({ channel, setEditingChannel, removeChannel }) {
    const { formState } = useFormContext();
    const { errors } = formState;

    if (!channel) {
        return null
    }
    return (
        <>
            <SingleConditionRow 
                title={channelConfig[channel]?.title}
                onEdit={() => {
                    setEditingChannel(channel)
                }}
                onRemove={() => removeChannel(channel)}
            />
            <ReactHookFormErrorMessage errors={errors} id={`recurringMessage.channel.${channel}`} />
        </>
    )
}

function ChannelList({ setEditingChannel, originalChannels, setValue }) {
    const selectedChannels = originalChannels || []
    const channels = getSelectedChannels({ selectedChannels: selectedChannels })
    const removeChannel = (channel) => {
        let finalChannels = []
        console.log("removeChannel selectedChannels:", selectedChannels, channel)
        if (selectedChannels?.includes(MessageChannel.allWhatsapp)) {
            finalChannels = whatsappAvailableChannels.filter(item => item != channel)
        } else if (selectedChannels?.includes(MessageChannel.allWechat)) {
            finalChannels = wechatAvailableChannels.filter(item => item != channel)
        } else if (selectedChannels?.includes(MessageChannel.all)) {
            finalChannels = smsAvailableChannels.filter(item => item != channel)
        } else {
            finalChannels = selectedChannels.filter(item =>
                item !== channel &&
                ![MessageChannel.allWhatsapp, MessageChannel.allWechat, MessageChannel.all].includes(item)
            );
            if (channel === MessageChannel.pushAndInbox) {
                finalChannels = finalChannels.filter(item => ![MessageChannel.inbox, MessageChannel.push].includes(item))
            }
        }
        console.log("removeChannel finalChannels:", finalChannels)
        setValue(`recurringMessage.channels`, finalChannels, { shouldDirty: true })
    }
    return (
        <div>
            {channels.map(item => <SingleChannel
                key={`channel-${item}`}
                channel={item}
                setEditingChannel={setEditingChannel}
                removeChannel={removeChannel}
            />)}
        </div>

    )
}

function EditingChannelPop({ channel }) {
    console.log("EditingChannelPop channel:", channel)
    if (!channel) {
        return <></>
    }
    return <RecurringMessagePromptBody channel={channel} />
}

function ChannelsSelector({ originalChannels, setValue }) {
    // const languages = useSelector(state => ({ languages: state.language.allList }))
    const [editingChannel, setEditingChannel] = useState(null)

    return (
        <div>
            <label className="create-section-label title-label">
                Select message channel
            </label>
            <ChannelList
                originalChannels={originalChannels}
                setEditingChannel={setEditingChannel}
                setValue={setValue}
            />
            <AddChannel
                originalChannels={originalChannels}
                setValue={setValue}
            />
            <BasePrompt
                show={!!editingChannel}
                disableEnforceFocus
                hideCloseIcon
                customClass="recurring-message-modal-content"
                title={channelConfig[editingChannel]?.title}
                otherBody={() => <EditingChannelPop channel={editingChannel} />}
                disableClickBackground={true}
                rightButton={{
                    text: "Confirm",
                    action: (() => {
                        setEditingChannel(null)
                    }),
                }}
            />
        </div>
    )
}

function ScheduledTime({ scheduledTime, setValue }) {
    const [hours, minutes, seconds] = (scheduledTime || "00:00:00").split(':').map(str => parseInt(str, 10));
    const utcHours = (hours - 8 + 24) % 24;
    const newDateTime = new Date().setUTCHours(utcHours, minutes, seconds);
    return (
        <div className="scheduled-time-container">
            <label className="create-section-label title-label">
                Scheduled time
            </label>
            <label className="section-short-description">
                Select the time to deliver the message on the scheduled date
            </label>
            <CustomDateTimeSelect
                hideDate
                defaultTime={newDateTime}
                onTimeChange={(date) => {
                    const updatedScheduleDate = moment(date).utcOffset("+0800").format("HH:mm:ss")
                    console.log("onTimeChange:", date, updatedScheduleDate)
                    setValue('recurringMessage.scheduledTime', updatedScheduleDate, { shouldDirty: true });
                }}
            />

        </div>
    )
}
export default function RecurringMessages() {
    const formContext = useFormContext()
    const {
        setValue,
        watch,
        formState: { errors }
    } = formContext;
    const channels = watch("recurringMessage.channels")
    const scheduledTime = watch("recurringMessage.scheduledTime")
    const sendMessageNowFieldName = "recurringMessage.sendMessageNow";
    return (
        <div className="recurring-message-container">
            <ChannelsSelector originalChannels={channels} setValue={setValue} />
            <ScheduledTime scheduledTime={scheduledTime} setValue={setValue} />
            <ReactHookFormSwitch
                title="Send message now"
                valueFieldName={sendMessageNowFieldName}
                tips="Send the recurring message now (after campaign saved) to customers with birthdays in current month (the scheduled send time has already passed when this campaign was saved)."
            />
        </div>
    )
}